/**
 * Маршруты и охрана доступа.
 *
 * Токен проверяется синхронно из localStorage: страница входа не должна
 * мигать при обновлении уже авторизованной вкладки.
 */

import type { ReactNode } from "react";
import { lazy, Suspense, useEffect } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation, useMatches } from "react-router-dom";

import { hasToken } from "./api/client";
import { AppLayout } from "./components/AppLayout";
import { Loading } from "./components/Feedback";

export interface RouteHandle {
  title?: string;
  back?: boolean;
}

const LoginView = lazy(() => import("./views/LoginView"));
const OverviewView = lazy(() => import("./views/OverviewView"));
const TablesView = lazy(() => import("./views/TablesView"));
const TableDetailView = lazy(() => import("./views/TableDetailView"));
const JobsView = lazy(() => import("./views/JobsView"));
const JobDetailView = lazy(() => import("./views/JobDetailView"));
const RunsView = lazy(() => import("./views/RunsView"));
const RunDetailView = lazy(() => import("./views/RunDetailView"));
const TargetsView = lazy(() => import("./views/TargetsView"));
const SourcesView = lazy(() => import("./views/SourcesView"));
const DumpView = lazy(() => import("./views/DumpView"));

function Page({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<Loading block label="Загружаем раздел" />}>
      {children}
    </Suspense>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  if (!hasToken()) {
    const target = location.pathname + location.search;
    return (
      <Navigate
        to="/login"
        replace
        state={{ redirect: target === "/" ? null : target }}
      />
    );
  }
  return <>{children}</>;
}

function GuestOnly({ children }: { children: ReactNode }) {
  return hasToken() ? <Navigate to="/" replace /> : <>{children}</>;
}

function Title() {
  const matches = useMatches();
  const handle = (matches.at(-1)?.handle ?? {}) as RouteHandle;

  useEffect(() => {
    document.title = handle.title ? `${handle.title} — sl1ce` : "sl1ce";
  }, [handle.title]);

  return null;
}

export const routes: RouteObject[] = [
  {
    path: "/login",
    element: (
      <GuestOnly>
        <Title />
        <Page>
          <LoginView />
        </Page>
      </GuestOnly>
    ),
    handle: { title: "Вход" } satisfies RouteHandle,
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <Title />
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <Page>
            <OverviewView />
          </Page>
        ),
        handle: { title: "Обзор" } satisfies RouteHandle,
      },
      {
        path: "tables",
        element: (
          <Page>
            <TablesView />
          </Page>
        ),
        handle: { title: "Таблицы" } satisfies RouteHandle,
      },
      {
        path: "tables/:entitySet",
        element: (
          <Page>
            <TableDetailView />
          </Page>
        ),
        handle: { title: "Таблица", back: true } satisfies RouteHandle,
      },
      {
        path: "jobs",
        element: (
          <Page>
            <JobsView />
          </Page>
        ),
        handle: { title: "Задания" } satisfies RouteHandle,
      },
      {
        path: "jobs/:id",
        element: (
          <Page>
            <JobDetailView />
          </Page>
        ),
        handle: { title: "Задание", back: true } satisfies RouteHandle,
      },
      {
        path: "runs",
        element: (
          <Page>
            <RunsView />
          </Page>
        ),
        handle: { title: "Запуски" } satisfies RouteHandle,
      },
      {
        path: "runs/:id",
        element: (
          <Page>
            <RunDetailView />
          </Page>
        ),
        handle: { title: "Запуск", back: true } satisfies RouteHandle,
      },
      {
        path: "sources",
        element: (
          <Page>
            <SourcesView />
          </Page>
        ),
        handle: { title: "Базы 1С" } satisfies RouteHandle,
      },
      {
        path: "targets",
        element: (
          <Page>
            <TargetsView />
          </Page>
        ),
        handle: { title: "Приёмники" } satisfies RouteHandle,
      },
      {
        path: "dump",
        element: (
          <Page>
            <DumpView />
          </Page>
        ),
        handle: { title: "Дамп базы" } satisfies RouteHandle,
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
];
